import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import InsightsIcon from "@mui/icons-material/Insights";
import CallMadeIcon from "@mui/icons-material/CallMade";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { makeGETRequestOnUserService } from "../../Api/api";
import {
  cancelAdminLumpsum,
  cancelAdminSip,
  completeAdminLumpsumPayment,
  completeAdminSip,
  confirmAdminTransaction,
  createAdminRedemption,
  createAdminSwitch,
  fetchMFAdminDashboard,
  fetchMFCompanies,
  fetchMFSchemeDetails,
  fetchMFSchemes,
  fetchMFSchemesByFundName,
  getAdminInvestmentRule,
  fetchSipSkipInstructions,
  getMFErrorMessage,
  getAdminSipSkipDateRange,
  MFAdminDashboardData,
  MFInvestmentRule,
  MFRecord,
  MFSchemeDetails,
  MFScheme,
  MFTransactionSource,
  MFSipSkipDateRange,
  placeAdminLumpsum,
  isMFMandatePendingError,
  resumeAdminSip,
  skipAdminSip,
  startAdminSip,
} from "../../Api/mfAdminInvestments";
import { ensureMFPostbackBridge } from "../../Helpers/mfPostbackBridge";
import { mfStatusLines, mfStatusTone } from "../../Helpers/mfOrderStatus";
import { appContext } from "../../App";
import { hasAdminPermission } from "../../Helpers/adminPermissions";
import InvestorLeadGate from "../../components/MutualFunds/InvestorLeadGate";
import {
  isMfUnsupportedAccountType,
  mfUnsupportedAccountReason,
} from "../../Helpers/mfAccountEligibility";
import {
  formatMFInstallmentDay,
  getDefaultMFInstallmentDay,
  mfFrequencyNeedsInstallmentDay,
  normalizeMFFrequency,
} from "../../Helpers/mfSchedule";
import ScheduledPlanDialog from "../../components/MutualFunds/ScheduledPlanDialog";
import SipModificationDialog from "../../components/MutualFunds/SipModificationDialog";
import CapitalGainsPanel from "../../components/MutualFunds/CapitalGainsPanel";
import ExternalCasPanel from "../../components/MutualFunds/ExternalCasPanel";
import {
  MF_MODIFICATION_PENDING_REASON,
  fetchMFPlanModifications,
  hasPendingModification,
  mfModificationDisabledReason,
} from "../../Api/mfAdminPlanModification";
import type { MFPlanKind } from "../../Api/mfAdminPlans";
import KycCheckDialog, {
  MFInvestorStatus,
  MFNomineeDetails,
} from "../../components/MutualFunds/KycCheckDialog";
import styles from "./user-investments-dashboard.module.css";

interface RouteParams { userUuid: string; }
type InvestmentKind = "lumpsum" | "sip";
type TransactionKind = "redeem" | "switch";
type OrderActionMode = "cancel_lumpsum" | "cancel_sip" | "skip" | "resume";

interface SipScheduleState {
  loading: boolean;
  activeInstruction: MFRecord | null;
  error?: string;
}

interface Holding {
  isin: string;
  name: string;
  amc: string | null;
  category: string;
  logo?: string | null;
  invested: number;
  current: number;
  units: number;
  redeemableAmount: number | null;
  folios: Array<{ folioNumber: string; redeemableAmount: number | null }>;
  transactionRules: MFTransactionRules;
}

interface MFTransactionRules {
  minRedemptionAmount: number | null;
  redemptionAmountMultiple: number | null;
  minSwitchInAmount: number | null;
  switchInAmountMultiple: number | null;
  minSwitchOutAmount: number | null;
  switchOutAmountMultiple: number | null;
}

const positiveNumber = (value: unknown) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getTransactionRules = (details?: MFRecord | null): MFTransactionRules => ({
  minRedemptionAmount: positiveNumber(details?.MinRedemptionAmount),
  redemptionAmountMultiple: positiveNumber(details?.RedemptionMultipleAmount),
  minSwitchInAmount: positiveNumber(details?.MinSwitchAmount),
  switchInAmountMultiple: positiveNumber(details?.SwitchMultipleAmount),
  minSwitchOutAmount: positiveNumber(details?.MinSwitchOutAmount),
  switchOutAmountMultiple: positiveNumber(details?.SwitchOutMultipleAmount),
});

const normalizeAmcName = (value?: string | null) =>
  String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const currency = (value: number, decimals = 0) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: decimals,
  }).format(Number.isFinite(value) ? value : 0);

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const normalizedStatus = (record: MFRecord) =>
  String(record.webhook_status || record.status || record.state || "Pending")
    .replace(/[_-]+/g, " ")
    .trim();

const TONE_CLASS: Record<string, string> = {
  success: styles.statusSuccess,
  pending: styles.statusPending,
  failed: styles.statusFailed,
};

/**
 * Both statuses a record carries — its webhook status and its state — exactly
 * as the API returned them, each labelled (Helpers/mfOrderStatus, shared with
 * the MF Overview). Display only: eligibility keeps normalizedStatus.
 */
const StatusLines = ({ record }: { record: MFRecord }) => {
  const lines = mfStatusLines(record);
  return (
    <div className={styles.statusStack}>
      {lines.length
        ? lines.map((line) => (
            <div key={line.label} className={styles.statusLine}>
              <span className={styles.statusLabel}>{line.label}</span>
              {line.value ? (
                <span className={`${styles.status} ${styles.statusRaw} ${TONE_CLASS[mfStatusTone(line.value)]}`}>
                  {line.value}
                </span>
              ) : (
                <span className={styles.fundMeta}>—</span>
              )}
            </div>
          ))
        : "—"}
      {record.cancellation_scheduled_on && (
        <div className={styles.fundMeta}>
          Cancellation scheduled · {formatDate(record.cancellation_scheduled_on)}
        </div>
      )}
    </div>
  );
};

const extractHoldings = (reports: MFRecord | null): Holding[] => {
  const folios = reports?.investor_reports?.generate_holdings?.folios;
  if (!Array.isArray(folios)) return [];
  const details = new Map<string, any>();
  (reports?.schemes_details || []).forEach((item: any) => details.set(item.isin, item.data || {}));
  const aggregated = new Map<string, Holding>();
  folios.forEach((folio: any) => {
    (folio?.schemes || []).forEach((scheme: any) => {
      if (!scheme?.isin) return;
      const meta = details.get(scheme.isin) || {};
      const existing = aggregated.get(scheme.isin) || {
        isin: scheme.isin,
        name: scheme.name || meta.longSchemeDescrip || scheme.isin,
        amc: meta.amc_mst_new?.amc || meta.amc_mst_new?.fund || null,
        category:
          meta.sclass_mst?.classname ||
          meta.sclass_mst?.category ||
          meta.scheme_details?.AMFIType ||
          "Mutual fund",
        logo: meta.logo || null,
        invested: 0,
        current: 0,
        units: 0,
        redeemableAmount: 0,
        folios: [],
        transactionRules: getTransactionRules(meta.scheme_details),
      };
      const redeemable = scheme.market_value?.redeemable_amount;
      existing.invested += Number(scheme.invested_value?.amount || 0);
      existing.current += Number(scheme.market_value?.amount || 0);
      existing.units += Number(scheme.holdings?.units || 0);
      existing.redeemableAmount =
        redeemable == null || existing.redeemableAmount == null
          ? null
          : existing.redeemableAmount + Number(redeemable);
      existing.folios.push({
        folioNumber: String(folio.folio_number || ""),
        redeemableAmount: redeemable == null ? null : Number(redeemable),
      });
      aggregated.set(scheme.isin, existing);
    });
  });
  return Array.from(aggregated.values()).sort((a, b) => b.current - a.current);
};

const isSip = (order: MFRecord) =>
  order.order_type === "sip" || order.systematic === true || Boolean(order.purchase_plan_id);

const canCancelLumpsum = (order: MFRecord) => {
  if (
    isSip(order) || !order.purchase_id || order.cancelled_at || order.completed_at ||
    order.succeeded_at || Number(order.allotted_units || 0) > 0 || order.purchased_price != null
  ) return false;
  const state = [order.webhook_status, order.status, order.state]
    .map((value) => String(value || "").trim().toLowerCase().replace(/[_-]+/g, " "))
    .filter(Boolean)
    .join(" ");
  if (["processed", "success", "successful", "completed", "settled", "failed", "failure", "rejected", "cancelled", "reversed"].some((value) => state.includes(value))) return false;
  return ["open", "pending", "processing", "sent to exchange", "under review", "created", "review completed", "submitted", "confirmed", "activating", "initiated"].some((value) => state.includes(value));
};

const canCancelSip = (order: MFRecord) => {
  if (!isSip(order) || !order.purchase_plan_id || order.cancellation_scheduled_on) return false;
  const status = normalizedStatus(order).toLowerCase().replace(/[_-]+/g, " ");
  const state = String(order.state || "").toLowerCase().replace(/[_-]+/g, " ");
  return !["cancelled", "completed", "failed", "rejected", "reversed"].some(
    (value) => status.includes(value) || state.includes(value)
  );
};

const canManageSipSkip = (order: MFRecord) => {
  const frequency = normalizeMFFrequency(String(order.frequency || "").replace(/[- ]/g, "_"));
  const installmentDay = Number(order.installment_day);
  return isSip(order) &&
    order.systematic === true &&
    Boolean(order.purchase_plan_id) &&
    String(order.state || "").trim().toLowerCase() === "active" &&
    !order.cancellation_scheduled_on &&
    order.auto_generate_installments === true &&
    ["monthly", "quarterly", "half_yearly", "yearly"].includes(frequency) &&
    Number(order.remaining_installments) !== 0 &&
    Number.isInteger(installmentDay) && installmentDay >= 1 && installmentDay <= 31;
};

/*
 * Why an action is unavailable, in one sentence — the user platform disables a
 * CTA and explains it rather than hiding it, because a missing button leaves an
 * operator guessing whether the option exists at all. Empty string means the
 * action is allowed.
 */
const lumpsumCancelReason = (order: MFRecord): string => {
  if (isSip(order)) return "";
  if (!order.purchase_id) return "This order has no purchase ID to cancel.";
  if (order.cancelled_at) return "This order is already cancelled.";
  if (order.completed_at || order.succeeded_at) return "This order has already completed.";
  if (Number(order.allotted_units || 0) > 0 || order.purchased_price != null) {
    return "Units are already allotted, so this order can no longer be cancelled.";
  }
  const state = [order.webhook_status, order.status, order.state]
    .map((value) => String(value || "").trim().toLowerCase().replace(/[_-]+/g, " "))
    .filter(Boolean)
    .join(" ");
  if (["processed", "success", "successful", "completed", "settled"].some((value) => state.includes(value))) {
    return "This order has already settled.";
  }
  if (["failed", "failure", "rejected", "cancelled", "reversed"].some((value) => state.includes(value))) {
    return "This order is already in a final state.";
  }
  return canCancelLumpsum(order) ? "" : "This order is not in a cancellable state.";
};

const sipCancelReason = (order: MFRecord): string => {
  if (!isSip(order)) return "";
  if (!order.purchase_plan_id) return "This SIP has no purchase plan to cancel.";
  if (order.cancellation_scheduled_on) return "A cancellation is already scheduled for this SIP.";
  return canCancelSip(order) ? "" : "This SIP is already in a final state.";
};

/**
 * Skip eligibility, with the reason. `getAdminSipSkipDateRange` already throws
 * the specific sentence for every ineligible case, so it is the source of truth
 * rather than a second copy of the rules.
 */
const sipSkipReason = (order: MFRecord): string => {
  if (!isSip(order)) return "Only a SIP can skip an installment.";
  if (!canManageSipSkip(order)) {
    if (!order.purchase_plan_id) return "This SIP has no purchase plan.";
    if (String(order.state || "").trim().toLowerCase() !== "active") return "This SIP is not active.";
    if (order.cancellation_scheduled_on) return "A cancellation is already scheduled for this SIP.";
    if (order.auto_generate_installments !== true) {
      return "Automatic installment generation is not enabled for this SIP.";
    }
    if (Number(order.remaining_installments) === 0) return "This SIP has no remaining installments.";
    return "This SIP frequency does not support skipping.";
  }
  try {
    getAdminSipSkipDateRange(order);
    return "";
  } catch (error) {
    return getMFErrorMessage(error, "This installment cannot be skipped right now.");
  }
};

const getActiveSipSkipInstruction = (instructions: MFRecord[]) =>
  instructions.find((instruction) =>
    ["pending", "active", "cancellation_requested"].includes(
      String(instruction.state || "").trim().toLowerCase().replace(/[- ]/g, "_")
    )
  ) || null;

const findTransactionSources = (holding: Holding, orders: MFRecord[]): MFTransactionSource[] => {
  const normalizedIsin = holding.isin.trim().toUpperCase();
  const folios = new Map(holding.folios
    .filter((folio) => folio.folioNumber.trim() && folio.redeemableAmount != null && folio.redeemableAmount > 0)
    .map((folio) => [folio.folioNumber.trim().toUpperCase(), folio]));
  return orders
    .filter((order) => {
      const orderIsin = String(order.scheme || order.isin || "").trim().toUpperCase();
      const folio = String(order.folio_number || "").trim().toUpperCase();
      if (orderIsin !== normalizedIsin || !folios.has(folio)) return false;
      const status = normalizedStatus(order).toLowerCase().replace(/[_-]+/g, " ");
      const successful = ["processed", "success", "successful", "completed", "settled", "active"]
        .some((value) => status.includes(value));
      const cancelledSip = isSip(order) && /\bcancell?/.test(status);
      return successful || cancelledSip;
    })
    .sort((left, right) => {
      const priority = (order: MFRecord) => {
        const status = normalizedStatus(order).toLowerCase().replace(/[_-]+/g, " ");
        return ["processed", "success", "successful", "completed", "settled", "active"]
          .some((value) => status.includes(value)) ? 2 : 1;
      };
      return priority(right) - priority(left) ||
        new Date(right.created_at || right.inserted_at || 0).getTime() -
        new Date(left.created_at || left.inserted_at || 0).getTime();
    })
    .map((order): MFTransactionSource | null => {
      const normalizedFolio = String(order.folio_number || "").trim().toUpperCase();
      const folio = folios.get(normalizedFolio);
      if (!folio) return null;
      const sipOrder = isSip(order);
      const fallbackId = String(order.id || "");
      const id = String(sipOrder
        ? order.purchase_plan_id || (fallbackId.startsWith("mfpp_") ? fallbackId : "")
        : order.purchase_id || (fallbackId.startsWith("mfp_") ? fallbackId : ""));
      if (!id) return null;
      return {
        id,
        idField: sipOrder ? "purchase_plan_id" : "purchase_id",
        folioNumber: folio.folioNumber,
        redeemableAmount: folio.redeemableAmount,
      };
    })
    .filter((source): source is MFTransactionSource => Boolean(source))
    .filter((source, index, allSources) =>
      allSources.findIndex((candidate) => candidate.folioNumber === source.folioNumber) === index
    );
};

const isAmountMultiple = (amount: number, multiple: number) => {
  if (!Number.isFinite(amount) || !Number.isFinite(multiple) || multiple <= 0) return false;
  const quotient = amount / multiple;
  return Math.abs(quotient - Math.round(quotient)) < 1e-8;
};

const decimalPlaces = (value: number) => {
  const text = String(value);
  if (text.includes("e-")) return Number(text.split("e-")[1]) || 0;
  return text.includes(".") ? text.split(".")[1].length : 0;
};

const greatestCommonDivisor = (left: number, right: number): number => {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b) [a, b] = [b, a % b];
  return a || 1;
};

const combineAmountMultiples = (...values: number[]) => {
  const multiples = values.filter((value) => Number.isFinite(value) && value > 0);
  if (!multiples.length) return 1;
  const scale = 10 ** Math.max(...multiples.map(decimalPlaces));
  const integers = multiples.map((value) => Math.round(value * scale));
  const combined = integers.reduce(
    (result, value) => Math.abs(result * value) / greatestCommonDivisor(result, value)
  );
  return combined / scale;
};

const initials = (value: string) =>
  value.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]).join("").toUpperCase();

const UserInvestmentsDashboard: React.FC = () => {
  const { userUuid } = useParams<RouteParams>();
  const { authUser } = useContext(appContext);
  // The planner is its own permission; without it the route refuses entry, so
  // the way in is shown disabled with the reason rather than as a dead end.
  const plannerBlockedReason = hasAdminPermission(authUser, "MF Portfolio Planner")
    ? ""
    : "Your admin account does not have the MF Portfolio Planner permission. A superuser can grant it from Admin Users.";
  const location = useLocation();
  const history = useHistory();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const user = useMemo(
    () => ({
      name: query.get("name") || "MF investor",
      email: query.get("email") || "",
      mobile: query.get("mobile") || "",
      smestId: query.get("smestId") || "",
      profileId: query.get("profileId") || "",
    }),
    [query]
  );
  const [data, setData] = useState<MFAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [schemes, setSchemes] = useState<MFScheme[]>([]);
  const [schemesLoading, setSchemesLoading] = useState(false);
  const [investmentKind, setInvestmentKind] = useState<InvestmentKind | null>(null);
  const [transaction, setTransaction] = useState<{ kind: TransactionKind; holding: Holding } | null>(null);
  const [orderAction, setOrderAction] = useState<{
    mode: OrderActionMode;
    order: MFRecord;
    skipInstruction?: MFRecord;
    skipDateRange?: MFSipSkipDateRange;
  } | null>(null);
  const [sipScheduleStates, setSipScheduleStates] = useState<Record<string, SipScheduleState>>({});
  const [scheduledPlanKind, setScheduledPlanKind] = useState<MFPlanKind | null>(null);
  const [modifyingSip, setModifyingSip] = useState<MFRecord | null>(null);
  // This investor's SIP modification instructions, so "Modify amount" can say
  // in advance that a change is already in flight — as the user platform does.
  const [modificationHistory, setModificationHistory] = useState<MFRecord[]>([]);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [investorStatus, setInvestorStatus] = useState<MFInvestorStatus>();
  const [accountType, setAccountType] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [investorStatusLoaded, setInvestorStatusLoaded] = useState(false);
  const [nomineeDetails, setNomineeDetails] = useState<MFNomineeDetails | null>(null);
  const [preparingKyc, setPreparingKyc] = useState(false);
  const [preparingAction, setPreparingAction] = useState("");
  const [callbackMessage, setCallbackMessage] = useState("");
  const [callbackError, setCallbackError] = useState("");
  const [callbackProcessing, setCallbackProcessing] = useState(false);
  const [callbackCanRetry, setCallbackCanRetry] = useState(false);
  const [callbackRetrySignal, setCallbackRetrySignal] = useState(0);
  const completedCallbacks = useRef(new Set<string>());
  const autoOpenedKyc = useRef(false);
  const toastr = require("toastr");

  const refreshInvestorStatus = useCallback(async (): Promise<MFInvestorStatus | undefined> => {
    try {
      const response = await makeGETRequestOnUserService(`/user/get/${encodeURIComponent(userUuid)}/`);
      const status = response?.data?.mfInvestorStatus as MFInvestorStatus | undefined;
      setInvestorStatus(status);
      setAccountType(response?.data?.accountType || "");
      setPanNumber(response?.data?.panCard?.panCardNumber || "");
      setNomineeDetails((response?.data?.nomineeDetails as MFNomineeDetails | null) ?? null);
      setInvestorStatusLoaded(true);
      return status;
    } catch (error) {
      console.error("Failed to load MF KYC status", error);
      setInvestorStatusLoaded(true);
      return undefined;
    }
  }, [userUuid]);

  const loadDashboard = useCallback(async (initial = false) => {
    initial ? setLoading(true) : setRefreshing(true);
    try {
      const next = await fetchMFAdminDashboard(userUuid);
      setData(next);
    } catch (error) {
      toastr.error(getMFErrorMessage(error, "Unable to load this MF dashboard."));
    } finally {
      initial ? setLoading(false) : setRefreshing(false);
    }
  }, [toastr, userUuid]);

  const ensureSchemes = useCallback(async () => {
    if (schemes.length) return schemes;
    setSchemesLoading(true);
    try {
      const next = await fetchMFSchemes();
      setSchemes(next);
      return next;
    } finally {
      setSchemesLoading(false);
    }
  }, [schemes]);

  useEffect(() => {
    loadDashboard(true);
    refreshInvestorStatus();
  }, [loadDashboard, refreshInvestorStatus]);

  // Re-read whenever the dashboard does, so the SIP list and its in-flight
  // changes never disagree. A failed read keeps the last known history: the
  // dialog checks again before anything starts, so this only decides whether
  // the reason can be shown on the button in advance.
  useEffect(() => {
    if (!data) return undefined;
    let active = true;
    fetchMFPlanModifications(userUuid)
      .then((instructions) => {
        if (active) setModificationHistory(instructions);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [data, userUuid]);

  const sipModificationReason = useCallback(
    (order: MFRecord) =>
      mfModificationDisabledReason(order) ||
      (hasPendingModification(modificationHistory, String(order.purchase_plan_id || ""))
        ? MF_MODIFICATION_PENDING_REASON
        : ""),
    [modificationHistory]
  );

  useEffect(() => {
    if (
      query.get("startKyc") !== "true" ||
      !investorStatusLoaded ||
      autoOpenedKyc.current ||
      investorStatus?.are_MF_KYC_Completed === true
    ) return;
    autoOpenedKyc.current = true;
    setKycDialogOpen(true);
  }, [investorStatus, investorStatusLoaded, query]);

  useEffect(() => {
    const paymentId = query.get("mf_admin_payment");
    const mandateId = query.get("mf_admin_sip_mandate");
    const callbackId = paymentId ? `payment:${paymentId}` : mandateId ? `sip:${mandateId}` : "";
    if (!callbackId || completedCallbacks.current.has(callbackId)) return;
    completedCallbacks.current.add(callbackId);
    setCallbackError("");
    setCallbackCanRetry(false);
    setCallbackProcessing(true);
    setCallbackMessage(paymentId ? "Reconciling completed payment…" : "Completing SIP after bank authorization…");

    const complete = async () => {
      try {
        if (paymentId) {
          await completeAdminLumpsumPayment(paymentId);
          setCallbackMessage("Payment reconciled and the lumpsum order was refreshed.");
        } else if (mandateId) {
          const allSchemes = await ensureSchemes();
          const isin = query.get("mf_admin_sip_isin") || "";
          const scheme = allSchemes.find((item) => item.isin === isin);
          if (!scheme) throw new Error("The SIP scheme could not be restored after authorization.");
          const completedSip = await completeAdminSip({
            userUuid,
            mandateId,
            scheme,
            amount: Number(query.get("mf_admin_sip_amount")),
            frequency: query.get("mf_admin_sip_frequency") || "monthly",
            installmentDay: Number(query.get("mf_admin_sip_day")) || undefined,
            installments: Number(query.get("mf_admin_sip_installments")),
            firstInstallmentNow: query.get("mf_admin_sip_first") === "true",
          });
          setCallbackMessage(completedSip.syncWarning
            ? "The SIP is confirmed. Its latest status could not be synchronized yet; refresh Orders shortly."
            : "The mandate was approved and the SIP was created successfully.");
        }
        await loadDashboard(false);
        const clean = new URLSearchParams(location.search);
        Array.from(clean.keys()).filter((key) => key.startsWith("mf_admin_")).forEach((key) => clean.delete(key));
        const suffix = clean.toString();
        history.replace(`${location.pathname}${suffix ? `?${suffix}` : ""}`);
      } catch (error) {
        setCallbackMessage("");
        setCallbackError(getMFErrorMessage(error));
        if (isMFMandatePendingError(error)) {
          completedCallbacks.current.delete(callbackId);
          setCallbackCanRetry(true);
        } else {
          const clean = new URLSearchParams(location.search);
          Array.from(clean.keys()).filter((key) => key.startsWith("mf_admin_")).forEach((key) => clean.delete(key));
          const suffix = clean.toString();
          history.replace(`${location.pathname}${suffix ? `?${suffix}` : ""}`);
        }
      } finally {
        setCallbackProcessing(false);
      }
    };
    complete();
  }, [callbackRetrySignal, ensureSchemes, history, loadDashboard, location.pathname, location.search, query, userUuid]);

  const holdings = useMemo(() => extractHoldings(data?.reports || null), [data?.reports]);
  /* A scheduled plan is keyed on a folio, so the dialog needs the folio list
     and enough labelling to show which fund each one belongs to. */
  const folioContext = useMemo(() => {
    const numbers: string[] = [];
    const fundNameByFolio: Record<string, string> = {};
    const amcByFolio: Record<string, string> = {};
    holdings.forEach((holding) => {
      holding.folios.forEach((folio) => {
        const number = String(folio.folioNumber || "").trim();
        if (!number || numbers.indexOf(number) >= 0) return;
        numbers.push(number);
        fundNameByFolio[number] = holding.name;
        amcByFolio[number] = holding.amc || "";
      });
    });
    return { numbers, fundNameByFolio, amcByFolio };
  }, [holdings]);
  const orders = useMemo(() => {
    const lumpsum = (data?.purchases || []).map((order) => ({ ...order, order_type: "lumpsum" }));
    const sip = (data?.sipPlans || []).map((order) => ({ ...order, order_type: "sip" }));
    return [...lumpsum, ...sip].sort(
      (a, b) => new Date(b.created_at || b.inserted_at || 0).getTime() - new Date(a.created_at || a.inserted_at || 0).getTime()
    );
  }, [data]);
  const totals = useMemo(() => {
    const invested = holdings.reduce((sum, holding) => sum + holding.invested, 0);
    const current = holdings.reduce((sum, holding) => sum + holding.current, 0);
    return { invested, current, gain: current - invested };
  }, [holdings]);
  const activeSips = (data?.sipPlans || []).filter((plan) => {
    const status = normalizedStatus(plan).toLowerCase();
    return !["cancelled", "failed", "completed", "rejected"].some((value) => status.includes(value));
  }).length;
  const accountUnsupported = isMfUnsupportedAccountType(accountType);
  // An unsupported account type closes every MF action regardless of KYC — the
  // product is not offered to it, so the gateway would refuse anything placed.
  const kycComplete =
    investorStatus?.are_MF_KYC_Completed === true && !accountUnsupported;

  useEffect(() => {
    if (activeTab !== "orders") return;
    const manageablePlans = orders.filter(canManageSipSkip);
    if (!manageablePlans.length) {
      setSipScheduleStates({});
      return;
    }

    let active = true;
    setSipScheduleStates((current) => {
      const loadingStates = { ...current };
      manageablePlans.forEach((order) => {
        loadingStates[order.purchase_plan_id] = {
          loading: true,
          activeInstruction: current[order.purchase_plan_id]?.activeInstruction || null,
        };
      });
      return loadingStates;
    });

    manageablePlans.forEach((order) => {
      const planId = String(order.purchase_plan_id);
      fetchSipSkipInstructions(planId)
        .then((instructions) => {
          if (!active) return;
          setSipScheduleStates((current) => ({
            ...current,
            [planId]: {
              loading: false,
              activeInstruction: getActiveSipSkipInstruction(instructions),
            },
          }));
        })
        .catch((error) => {
          if (!active) return;
          setSipScheduleStates((current) => ({
            ...current,
            [planId]: {
              loading: false,
              activeInstruction: current[planId]?.activeInstruction || null,
              error: getMFErrorMessage(error, "Unable to check the SIP skip status."),
            },
          }));
        });
    });

    return () => { active = false; };
  }, [activeTab, orders]);

  const prepareKyc = async () => {
    setPreparingKyc(true);
    const latest = await refreshInvestorStatus();
    setPreparingKyc(false);
    if (!latest) {
      toastr.error("Unable to load this user's MF KYC readiness.");
      return;
    }
    setKycDialogOpen(true);
  };

  const openInvestment = async (kind: InvestmentKind) => {
    const actionKey = `investment:${kind}`;
    setPreparingAction(actionKey);
    try {
      await ensureSchemes();
      setInvestmentKind(kind);
    } catch (error) {
      toastr.error(getMFErrorMessage(error, "Unable to load mutual fund schemes."));
    } finally {
      setPreparingAction("");
    }
  };

  const openTransaction = (kind: TransactionKind, holding: Holding) => {
    setTransaction({ kind, holding });
  };

  /**
   * The Portfolio Planner, already scoped to this investor.
   *
   * Opened in a new tab on purpose: this dashboard may be mid-callback or
   * holding unsaved dialog state, and the planner's own order flow uses popups
   * that expect to return to their own opener. The uuid in the path is the
   * authoritative identity; the query string is display text only.
   */
  const openPortfolioPlanner = () => {
    const params = new URLSearchParams();
    if (user.name) params.set("name", user.name);
    if (user.email) params.set("email", user.email);
    if (user.mobile) params.set("mobile", user.mobile);
    if (user.smestId) params.set("smestId", user.smestId);
    const suffix = params.toString();
    window.open(
      `/mf_portfolio_planner/${encodeURIComponent(userUuid)}${suffix ? `?${suffix}` : ""}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openSipSchedule = async (order: MFRecord) => {
    const actionKey = `schedule:${order.purchase_plan_id}`;
    setPreparingAction(actionKey);
    try {
      const instructions = await fetchSipSkipInstructions(order.purchase_plan_id);
      const active = getActiveSipSkipInstruction(instructions);
      setSipScheduleStates((current) => ({
        ...current,
        [order.purchase_plan_id]: { loading: false, activeInstruction: active },
      }));
      if (active) {
        setOrderAction({ mode: "resume", order, skipInstruction: active });
      } else {
        setOrderAction({ mode: "skip", order, skipDateRange: getAdminSipSkipDateRange(order) });
      }
    } catch (error) {
      toastr.error(getMFErrorMessage(error, "Unable to load SIP schedule instructions."));
    } finally {
      setPreparingAction("");
    }
  };

  if (loading) {
    return <div className={styles.loadingPage}><div><div className={styles.spinner} />Loading investor dashboard…</div></div>;
  }

  const transactionActivity = [
    ...(data?.redemptions || []).map((item) => ({ ...item, activityKind: "Redemption" })),
    ...(data?.switches || []).map((item) => ({ ...item, activityKind: "Switch" })),
  ].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());

  return (
    <main className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.topbar}>
          <button className={styles.backButton} onClick={() => window.close()}>
            <ArrowBackIcon fontSize="small" /> Close dashboard
          </button>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span className={styles.adminBadge}>ADMIN · ON BEHALF OF USER</span>
            <button className={styles.refreshButton} disabled={refreshing} onClick={() => loadDashboard(false)}>
              {refreshing ? <InlineLoader /> : <RefreshIcon fontSize="small" />} {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <p className={styles.eyebrow}>Mutual fund workspace</p>
            <h1>{user.name}</h1>
            <div className={styles.identity}>
              {user.smestId && <span>SMEST ID · {user.smestId}</span>}
              {user.email && <span>{user.email}</span>}
              {user.mobile && <span>+91 {user.mobile}</span>}
            </div>
            <div className={`${styles.profileState} ${kycComplete ? styles.ready : styles.pending}`}>
              {kycComplete ? <CheckCircleOutlineIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
              {kycComplete ? "MF KYC complete" : "MF onboarding required"}
            </div>
          </div>
          <div className={styles.heroAside}>
            <button className={styles.primaryButton} disabled={!kycComplete || schemesLoading || Boolean(preparingAction)} onClick={() => openInvestment("lumpsum")}>
              {preparingAction === "investment:lumpsum" ? <InlineLoader /> : <AddCircleOutlineIcon fontSize="small" />} {preparingAction === "investment:lumpsum" ? "Loading schemes…" : "New investment"}
            </button>
            <button
              className={styles.secondaryButton}
              disabled={Boolean(plannerBlockedReason)}
              title={plannerBlockedReason || undefined}
              aria-label={plannerBlockedReason ? `Portfolio Planner — unavailable: ${plannerBlockedReason}` : undefined}
              onClick={openPortfolioPlanner}
            >
              <InsightsIcon fontSize="small" /> Portfolio Planner
            </button>
            {accountUnsupported && (
          <div className={styles.warning} style={{ marginTop: 18 }}>
            {mfUnsupportedAccountReason(accountType)}
          </div>
        )}

        {!kycComplete && !accountUnsupported && (
              <button className={styles.secondaryButton} disabled={preparingKyc} onClick={prepareKyc}>
                {preparingKyc && <InlineLoader />} {preparingKyc ? "Preparing KYC…" : "Complete MF KYC"}
              </button>
            )}
          </div>
        </section>

        {!kycComplete && (
          <section className={styles.kycBanner}>
            <div><h3>Finish this investor’s MF setup here</h3><p>The KYC, bank, nominee, investment-account, signature and eSign flow now belongs to this dashboard.</p></div>
            <button className={styles.kycButton} disabled={preparingKyc} onClick={prepareKyc}>{preparingKyc && <InlineLoader />} {preparingKyc ? "Checking status…" : "Start / resume KYC"}</button>
          </section>
        )}

        {callbackMessage && <div className={styles.info} style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 8 }}>{callbackProcessing && <InlineLoader />}{callbackMessage}</div>}
        {callbackError && <div className={styles.error} style={{ marginTop: 18 }}>{callbackError}{callbackCanRetry ? <button className={styles.miniButton} disabled={callbackProcessing} style={{ marginLeft: 12 }} onClick={() => setCallbackRetrySignal((value) => value + 1)}>{callbackProcessing && <InlineLoader />} Retry status check</button> : " Check Orders before starting the operation again."}</div>}
        {data?.warnings.length ? <div className={styles.warning} style={{ marginTop: 18 }}>Some sections could not be loaded: {data.warnings.join(" · ")}</div> : null}

        <section className={styles.metricGrid}>
          <MetricCard label="Current value" value={currency(totals.current)} hint={`${holdings.length} schemes`} icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />} />
          <MetricCard label="Invested value" value={currency(totals.invested)} hint="Across available folios" icon={<ReceiptLongOutlinedIcon fontSize="small" />} />
          <MetricCard label="Total gain / loss" value={currency(totals.gain)} hint={totals.invested ? `${((totals.gain / totals.invested) * 100).toFixed(2)}% absolute` : "No holdings yet"} icon={<TrendingUpIcon fontSize="small" />} tone={totals.gain >= 0 ? "positive" : "negative"} />
          <MetricCard label="Active SIPs" value={String(activeSips)} hint={`${orders.length} total orders`} icon={<AutorenewIcon fontSize="small" />} />
        </section>

        <section className={styles.actionGrid}>
          <ActionCard icon={<PaymentsOutlinedIcon />} title="Place lumpsum" subtitle="Create a one-time purchase" disabled={!kycComplete || Boolean(preparingAction)} loading={preparingAction === "investment:lumpsum"} onClick={() => openInvestment("lumpsum")} />
          <ActionCard icon={<AutorenewIcon />} title="Start SIP" subtitle="Set up mandate and schedule" disabled={!kycComplete || Boolean(preparingAction)} loading={preparingAction === "investment:sip"} onClick={() => openInvestment("sip")} />
          <ActionCard icon={<AccountBalanceWalletOutlinedIcon />} title="Redeem" subtitle="Choose from current holdings" disabled={!kycComplete || !holdings.length || Boolean(preparingAction)} onClick={() => setActiveTab("holdings")} />
          <ActionCard icon={<SwapHorizIcon />} title="Switch fund" subtitle="Move within the same AMC" disabled={!kycComplete || !holdings.length || Boolean(preparingAction)} onClick={() => setActiveTab("holdings")} />
          <ActionCard icon={<CallMadeIcon />} title="Withdrawal plan (SWP)" subtitle="Schedule redemptions from a folio" disabled={!kycComplete || !holdings.length || Boolean(preparingAction)} onClick={() => setScheduledPlanKind("swp")} />
          <ActionCard icon={<SyncAltIcon />} title="Transfer plan (STP)" subtitle="Schedule switches within an AMC" disabled={!kycComplete || !holdings.length || Boolean(preparingAction)} onClick={() => setScheduledPlanKind("stp")} />
          <ActionCard icon={<InsightsIcon />} title="Portfolio Planner" subtitle="Goal plan, report and recommended orders" reason={plannerBlockedReason} onClick={openPortfolioPlanner} />
        </section>

        <section className={styles.contentCard}>
          <nav className={styles.tabs} aria-label="Investment dashboard sections">
            {[
              ["overview", "Overview"], ["holdings", "Holdings"], ["orders", "Orders & SIPs"], ["transactions", "Transactions"], ["gains", "Capital gains"], ["external", "External holdings"],
            ].map(([key, label]) => (
              <button key={key} className={`${styles.tab} ${activeTab === key ? styles.activeTab : ""}`} onClick={() => setActiveTab(key)}>{label}</button>
            ))}
          </nav>
          <div className={styles.panel}>
            {activeTab === "overview" && (
              <>
                <SectionHeader title="Portfolio snapshot" subtitle="Holdings and recent activity available from the user MF reports." />
                {holdings.length ? <HoldingsTable holdings={holdings.slice(0, 4)} readOnly /> : <EmptyState text="No mutual fund holdings are available for this investor yet." />}
              </>
            )}
            {activeTab === "holdings" && (
              <>
                <SectionHeader title="Current holdings" subtitle="Redemption and switch actions use this investor’s eligible folios." />
                {holdings.length ? (
                  <HoldingsTable holdings={holdings} onRedeem={(holding) => openTransaction("redeem", holding)} onSwitch={(holding) => openTransaction("switch", holding)} disabled={!kycComplete || Boolean(preparingAction)} busyAction={preparingAction} />
                ) : <EmptyState text="No holdings were returned for this investor." />}
              </>
            )}
            {activeTab === "orders" && (
              <>
                <SectionHeader title="Orders and SIP instructions" subtitle="Cancel pending purchases, cancel SIPs, or manage the next installment." />
                {orders.length ? (
                  <OrdersTable
                    orders={orders}
                    sipScheduleStates={sipScheduleStates}
                    disabled={!kycComplete || Boolean(preparingAction)}
                    busyAction={preparingAction}
                    onCancel={(order) => setOrderAction({ mode: isSip(order) ? "cancel_sip" : "cancel_lumpsum", order })}
                    onSchedule={openSipSchedule}
                    onModify={(order: MFRecord) => setModifyingSip(order)}
                    modificationReason={sipModificationReason}
                  />
                ) : <EmptyState text="No lumpsum or SIP orders were returned for this investor." />}
              </>
            )}
            {activeTab === "external" && (
              <ExternalCasPanel
                userUuid={userUuid}
                investorEmail={user.email}
                investorPan={panNumber}
              />
            )}
            {activeTab === "gains" && (
              <CapitalGainsPanel
                reports={data?.reports || null}
                investorName={user.name}
                pan={panNumber}
                accountRef={user.smestId}
              />
            )}
            {activeTab === "transactions" && (
              <>
                <SectionHeader title="Redemptions and switches" subtitle="A consolidated audit view of servicing transactions." />
                {transactionActivity.length ? <ActivityTable items={transactionActivity} /> : <EmptyState text="No redemption or switch transactions are available." />}
              </>
            )}
          </div>
        </section>
      </div>

      <InvestmentDialog
        open={Boolean(investmentKind)} kind={investmentKind || "lumpsum"} userUuid={userUuid}
        schemes={schemes} onClose={() => setInvestmentKind(null)} onComplete={() => loadDashboard(false)}
      />
      {transaction && (
        <TransactionDialog
          open kind={transaction.kind} holding={transaction.holding}
          sources={findTransactionSources(transaction.holding, orders)}
          onClose={() => setTransaction(null)} onComplete={() => loadDashboard(false)}
        />
      )}
      {orderAction && (
        <OrderActionDialog
          open action={orderAction} onClose={() => setOrderAction(null)}
          onComplete={() => loadDashboard(false)}
        />
      )}
      {modifyingSip && (
        <SipModificationDialog
          open
          order={modifyingSip}
          userUuid={userUuid}
          returnPath={`${location.pathname}${location.search}`}
          onClose={() => setModifyingSip(null)}
          onComplete={() => loadDashboard(false)}
        />
      )}
      {scheduledPlanKind && (
        <ScheduledPlanDialog
          open
          kind={scheduledPlanKind}
          userUuid={userUuid}
          folioNumbers={folioContext.numbers}
          fundNameByFolio={folioContext.fundNameByFolio}
          amcByFolio={folioContext.amcByFolio}
          onClose={() => setScheduledPlanKind(null)}
          onComplete={() => loadDashboard(false)}
        />
      )}
      <KycCheckDialog
        open={kycDialogOpen} userUuid={userUuid} investorStatus={investorStatus}
        nomineeDetails={nomineeDetails} onClose={() => setKycDialogOpen(false)}
        onComplete={async () => { toastr.success("MF KYC completed successfully."); await refreshInvestorStatus(); await loadDashboard(false); }}
        onRefreshInvestorStatus={refreshInvestorStatus}
      />
    </main>
  );
};

const MetricCard = ({ label, value, hint, icon, tone }: any) => (
  <div className={styles.metricCard}>
    <div className={styles.metricTop}><span>{label}</span><span className={styles.metricIcon}>{icon}</span></div>
    <div className={`${styles.metricValue} ${tone ? styles[tone] : ""}`}>{value}</div><div className={styles.metricHint}>{hint}</div>
  </div>
);

const InlineLoader = () => <span className={styles.inlineSpinner} aria-hidden="true" />;

const ActionCard = ({ icon, title, subtitle, reason, disabled, loading, onClick }: any) => (
  <button className={styles.actionCard} disabled={Boolean(reason) || disabled || loading} title={reason || undefined} aria-label={reason ? `${title} — unavailable: ${reason}` : undefined} onClick={onClick} aria-busy={loading || undefined}>
    <span className={styles.actionIcon}>{loading ? <InlineLoader /> : icon}</span><span className={styles.actionText}><strong>{loading ? "Loading…" : title}</strong><small>{loading ? "Preparing this action" : subtitle}</small></span>
  </button>
);

const SectionHeader = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div className={styles.sectionHeader}><div><h2>{title}</h2><p>{subtitle}</p></div></div>
);

const EmptyState = ({ text }: { text: string }) => (
  <div className={styles.empty}><div className={styles.emptyIcon}><AccountBalanceWalletOutlinedIcon /></div>{text}</div>
);

/**
 * Whether a holding can be redeemed or switched at all.
 *
 * Both need a folio with a positive redeemable amount; without one there is
 * nothing to transact against, whatever the scheme permits.
 */
const holdingActionReason = (holding: Holding): string => {
  const redeemable = holding.folios.some(
    (folio) => folio.redeemableAmount != null && folio.redeemableAmount > 0
  );
  if (!redeemable) {
    return "No positive redeemable amount is currently available for this holding.";
  }
  return "";
};

const FundIdentity = ({ holding }: { holding: Holding }) => (
  <div className={styles.fundCell}>
    {holding.logo ? <img className={styles.fundLogo} src={holding.logo} alt="" /> : <span className={styles.fundLogo}>{initials(holding.name)}</span>}
    <div><div className={styles.fundName}>{holding.name}</div><div className={styles.fundMeta}>{holding.category} · {holding.isin}</div></div>
  </div>
);

const HoldingsTable = ({ holdings, onRedeem, onSwitch, disabled, readOnly, busyAction = "" }: any) => (
  <div className={styles.tableWrap}><table className={styles.table}><thead><tr><th>Fund</th><th>Invested</th><th>Current value</th><th>Gain / loss</th><th>Units</th>{!readOnly && <th>Actions</th>}</tr></thead><tbody>
    {holdings.map((holding: Holding) => {
      const gain = holding.current - holding.invested;
      const redeemBusy = busyAction === `transaction:redeem:${holding.isin}`;
      const switchBusy = busyAction === `transaction:switch:${holding.isin}`;
      return <tr key={holding.isin}><td><FundIdentity holding={holding} /></td><td>{currency(holding.invested)}</td><td><b>{currency(holding.current)}</b></td><td className={gain >= 0 ? styles.positive : styles.negative}>{currency(gain)}</td><td>{holding.units.toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>{!readOnly && <td><div className={styles.rowActions}><ActionButton label={redeemBusy ? "Loading…" : "Redeem"} reason={holdingActionReason(holding)} disabled={disabled} busy={redeemBusy} onClick={() => onRedeem(holding)} /><ActionButton label={switchBusy ? "Loading…" : "Switch"} reason={holdingActionReason(holding)} disabled={disabled} busy={switchBusy} onClick={() => onSwitch(holding)} /></div></td>}</tr>;
    })}
  </tbody></table></div>
);

/**
 * An action and, when it is unavailable, why.
 *
 * Hiding a control leaves an operator wondering whether the option exists at
 * all; disabling it with the reason answers that in place. Mirrors the user
 * platform's own pattern (see TaxTab's redeem button).
 */
const ActionButton = ({ label, reason, disabled, busy, danger, onClick }: any) => {
  const blocked = Boolean(reason);
  return (
    <button
      className={`${styles.miniButton} ${danger ? styles.miniDanger : ""} ${blocked ? styles.miniButtonBlocked : ""}`}
      disabled={blocked || disabled}
      title={reason || undefined}
      aria-label={blocked ? `${label} — unavailable: ${reason}` : label}
      onClick={onClick}
    >
      {busy && <InlineLoader />} {label}
    </button>
  );
};

const OrdersTable = ({ orders, sipScheduleStates, disabled, busyAction = "", onCancel, onSchedule, onModify, modificationReason = mfModificationDisabledReason }: any) => (
  <div className={styles.tableWrap}><table className={styles.table}><thead><tr><th>Scheme</th><th>Type</th><th>Amount</th><th>Status</th><th>Created</th><th>Next installment</th><th>Actions</th></tr></thead><tbody>
    {orders.map((order: MFRecord, index: number) => {
      const sipOrder = isSip(order);
      const scheduleBusy = busyAction === `schedule:${order.purchase_plan_id}`;
      const scheduleState: SipScheduleState | undefined = sipScheduleStates[order.purchase_plan_id];
      const checkingSchedule = !scheduleState || scheduleState.loading;
      const scheduleLabel = scheduleState?.activeInstruction
        ? "Resume installment"
        : "Skip installment";
      // A resume is always available while an instruction is live; only a fresh
      // skip has to satisfy the date-window rules.
      const skipReason = scheduleState?.activeInstruction ? "" : sipSkipReason(order);
      return <tr key={order.uuid || order.purchase_id || order.purchase_plan_id || index}><td><div className={styles.fundName}>{order.scheme_name || order.fund_name || order.scheme || "Mutual fund order"}</div><div className={styles.fundMeta}>{order.folio_number ? `Folio ${order.folio_number}` : order.purchase_id || order.purchase_plan_id}</div></td><td>{sipOrder ? "SIP" : "Lumpsum"}</td><td><b>{currency(Number(order.amount || 0))}</b>{sipOrder && <div className={styles.fundMeta}>{String(order.frequency || "").replace(/_/g, " ")}</div>}</td><td><StatusLines record={order} /></td><td>{formatDate(order.created_at || order.inserted_at)}</td><td>{sipOrder ? formatDate(order.next_installment_date) : "—"}</td><td><div className={styles.rowActions}>
        <ActionButton danger label="Cancel" reason={sipOrder ? sipCancelReason(order) : lumpsumCancelReason(order)} disabled={disabled} onClick={() => onCancel(order)} />
        {sipOrder && <ActionButton label="Modify amount" reason={modificationReason(order)} disabled={disabled} onClick={() => onModify(order)} />}
        {sipOrder && <ActionButton label={scheduleBusy ? "Loading…" : checkingSchedule ? "Checking status…" : scheduleLabel} reason={scheduleState?.error || skipReason} disabled={disabled || checkingSchedule} busy={scheduleBusy || checkingSchedule} onClick={() => onSchedule(order)} />}
      </div></td></tr>;
    })}
  </tbody></table></div>
);

const ActivityTable = ({ items }: { items: MFRecord[] }) => (
  <div className={styles.tableWrap}><table className={styles.table}><thead><tr><th>Transaction</th><th>Scheme</th><th>Folio</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead><tbody>{items.map((item, index) => { return <tr key={item.uuid || index}><td><b>{item.activityKind}</b></td><td>{item.scheme || item.switch_out_scheme || "—"}{item.activityKind === "Switch" && <div className={styles.fundMeta}>to {item.switch_in_scheme}</div>}</td><td>{item.folio_number || "—"}</td><td>{currency(Number(item.amount || item.redeemed_amount || item.switched_out_amount || 0))}</td><td><StatusLines record={item} /></td><td>{formatDate(item.created_at)}</td></tr>; })}</tbody></table></div>
);

const DialogHeading = ({ title, subtitle, onClose }: any) => (
  <div className={styles.dialogTitle}><div><h2>{title}</h2><p>{subtitle}</p></div><IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton></div>
);

const InvestmentDialog = ({ open, kind: initialKind, userUuid, schemes, onClose, onComplete }: any) => {
  const [kind, setKind] = useState<InvestmentKind>(initialKind);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<MFScheme | null>(null);
  const [selectedDetails, setSelectedDetails] = useState<MFSchemeDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [day, setDay] = useState(1);
  const [installments, setInstallments] = useState(12);
  const [firstNow, setFirstNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setKind(initialKind); setSelected(null); setSelectedDetails(null); setError(""); }, [initialKind]);
  const filtered = schemes.filter((scheme: MFScheme) => {
    const allowed = kind === "lumpsum" ? scheme.purchase_allowed === true : scheme.sip_allowed === true;
    return allowed && scheme.active !== false && !scheme.merged && `${scheme.name} ${scheme.isin}`.toLowerCase().includes(search.toLowerCase());
  }).slice(0, 80);
  const sipFrequencies = Array.from(new Set((selectedDetails?.scheme_plans_v2?.thresholds || [])
    .filter((threshold) => String(threshold.type || "").toLowerCase() === "sip" && threshold.frequency)
    .map((threshold) => String(threshold.frequency).trim().toLowerCase())));
  let rule: MFInvestmentRule | null = null;
  try {
    if (selectedDetails) rule = getAdminInvestmentRule(selectedDetails, kind, kind === "sip" ? frequency : undefined);
  } catch (_) {
    rule = null;
  }
  const minAmount = rule?.minimum ?? 0;
  const maxAmount = rule?.maximum ?? 0;
  const amountNumber = Number(amount);
  const amountMultiple = rule?.multiple ?? 1;
  const amountStep = combineAmountMultiples(amountMultiple, 10);
  const scheduleValid = kind === "lumpsum" || Boolean(
    rule && Number.isInteger(installments) && installments >= rule.installmentsMinimum &&
    (!mfFrequencyNeedsInstallmentDay(frequency) ||
      (Number.isInteger(day) && rule.dates.includes(day)))
  );
  const amountValid = Boolean(
    selected && selectedDetails && rule &&
    selectedDetails.data?.[kind === "lumpsum" ? "purchase_allowed" : "sip_allowed"] === true &&
    amountNumber > 0 &&
    amountNumber >= minAmount &&
    amountNumber <= maxAmount &&
    isAmountMultiple(amountNumber, amountStep) && scheduleValid
  );
  const selectScheme = async (scheme: MFScheme) => {
    setSelected(scheme); setSelectedDetails(null); setAmount(""); setError(""); setDetailsLoading(true);
    try {
      const details = await fetchMFSchemeDetails(scheme.isin);
      const allowed = details.data?.[kind === "lumpsum" ? "purchase_allowed" : "sip_allowed"] === true;
      if (!allowed || details.data.active === false || details.data.merged === true) {
        throw new Error(`This scheme is not currently accepting ${kind === "lumpsum" ? "lumpsum purchases" : "SIP investments"}.`);
      }
      setSelectedDetails(details);
      if (kind === "lumpsum") {
        const nextRule = getAdminInvestmentRule(details, "lumpsum");
        setAmount(String(nextRule.minimum));
      } else {
        const frequencies = (details.scheme_plans_v2?.thresholds || [])
          .filter((threshold) => String(threshold.type || "").toLowerCase() === "sip" && threshold.frequency)
          .map((threshold) => String(threshold.frequency).trim().toLowerCase());
        const nextFrequency = frequencies.includes("monthly") ? "monthly" : frequencies[0];
        if (!nextFrequency) throw new Error("This scheme did not return an eligible SIP frequency.");
        const nextRule = getAdminInvestmentRule(details, "sip", nextFrequency);
        setFrequency(nextFrequency);
        setDay(getDefaultMFInstallmentDay(nextFrequency, nextRule.dates) ?? nextRule.dates[0] ?? 1);
        setInstallments(Math.max(12, nextRule.installmentsMinimum));
        setAmount(String(nextRule.minimum));
      }
    } catch (requestError) {
      setError(getMFErrorMessage(requestError));
    } finally {
      setDetailsLoading(false);
    }
  };
  const changeFrequency = (nextFrequency: string) => {
    if (!selectedDetails) return;
    try {
      const nextRule = getAdminInvestmentRule(selectedDetails, "sip", nextFrequency);
      setFrequency(nextFrequency);
      setDay(getDefaultMFInstallmentDay(nextFrequency, nextRule.dates) ?? nextRule.dates[0] ?? 1);
      setInstallments((current) => Math.max(current, nextRule.installmentsMinimum));
      if (!isAmountMultiple(Number(amount), combineAmountMultiples(nextRule.multiple, 10)) || Number(amount) < nextRule.minimum || Number(amount) > nextRule.maximum) {
        setAmount(String(nextRule.minimum));
      }
      setError("");
    } catch (requestError) {
      setError(getMFErrorMessage(requestError));
    }
  };
  const submit = async () => {
    if (!selected || !amountValid) { setError(`Enter an eligible amount from ${currency(minAmount)} in multiples of ${currency(amountStep)}.`); return; }
    setSubmitting(true); setError("");
    try {
      await ensureMFPostbackBridge();
      const returnUrl = window.location.href;
      if (kind === "lumpsum") {
        const result = await placeAdminLumpsum({ userUuid, scheme: selected, amount: amountNumber, returnUrl });
        if (!result.redirectUrl) throw new Error("The order was created, but the payment URL was not returned.");
        window.location.assign(result.redirectUrl);
      } else {
        const result = await startAdminSip({ userUuid, scheme: selected, amount: amountNumber, frequency, installmentDay: mfFrequencyNeedsInstallmentDay(frequency) ? day : undefined, installments, firstInstallmentNow: firstNow, returnUrl });
        if (!result.redirectUrl) throw new Error("The mandate was created, but the bank authorization URL was not returned.");
        window.location.assign(result.redirectUrl);
      }
      onComplete();
    } catch (requestError) { setError(getMFErrorMessage(requestError)); setSubmitting(false); }
  };
  return <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm"><DialogContent><DialogHeading title={kind === "lumpsum" ? "Place lumpsum order" : "Start a new SIP"} subtitle="Admin instruction for this investor · no consent OTP" onClose={submitting ? undefined : onClose} /><div className={styles.form}>
    <div className={styles.segmented}><button className={`${styles.segment} ${kind === "lumpsum" ? styles.segmentActive : ""}`} onClick={() => { setKind("lumpsum"); setSelected(null); setSelectedDetails(null); }}>Lumpsum</button><button className={`${styles.segment} ${kind === "sip" ? styles.segmentActive : ""}`} onClick={() => { setKind("sip"); setSelected(null); setSelectedDetails(null); }}>SIP</button></div>
    <div className={styles.field}><label>Find an eligible scheme</label><div style={{ position: "relative" }}><SearchIcon style={{ position: "absolute", left: 11, top: 10, color: "#8b91a3", fontSize: 19 }} /><input className={styles.input} style={{ paddingLeft: 37 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by scheme name or ISIN" /></div></div>
    <div className={styles.schemeResults}>{filtered.length ? filtered.map((scheme: MFScheme) => <button type="button" key={scheme.fund_scheme_id || scheme.isin} disabled={detailsLoading} className={`${styles.schemeOption} ${selected?.isin === scheme.isin ? styles.schemeSelected : ""}`} onClick={() => selectScheme(scheme)}><strong>{scheme.name}</strong><small>{scheme.fund_category || "MF"} · {scheme.isin}</small></button>) : <div className={styles.empty} style={{ padding: 25 }}>No eligible schemes match this search.</div>}</div>
    {detailsLoading && <div className={styles.info}><InlineLoader /> Checking the authoritative scheme limits…</div>}
    {selected && selectedDetails && rule && <><div className={styles.field}><label>{kind === "sip" ? "Installment amount" : "Investment amount"}</label><input className={styles.input} type="number" min={minAmount} max={maxAmount} step={amountStep} value={amount} onChange={(e) => setAmount(e.target.value)} /><div className={styles.helper}>Minimum {currency(minAmount)} · Maximum {currency(maxAmount)} · Multiples of {currency(amountStep)}</div></div>
      {kind === "sip" && <><div className={styles.formRow}><div className={styles.field}><label>Frequency</label><select className={styles.select} value={frequency} onChange={(e) => changeFrequency(e.target.value)}>{sipFrequencies.map((value) => <option key={value} value={value}>{value.replace(/_/g, " ")}</option>)}</select></div><div className={styles.field}><label>{mfFrequencyNeedsInstallmentDay(frequency) ? "Debit day" : "Schedule"}</label>{mfFrequencyNeedsInstallmentDay(frequency) ? <select className={styles.select} value={day} onChange={(e) => setDay(Number(e.target.value))}>{(rule.dates.length ? rule.dates : [1]).map((value) => <option key={value} value={value}>{formatMFInstallmentDay(frequency, value)}</option>)}</select> : <div className={styles.helper} style={{ marginTop: 11 }}>This frequency debits every business day — no debit day is sent.</div>}</div></div><div className={styles.formRow}><div className={styles.field}><label>Number of installments</label><input className={styles.input} type="number" min={rule.installmentsMinimum} value={installments} onChange={(e) => setInstallments(Number(e.target.value))} /></div><div className={styles.field}><label>First installment</label><select className={styles.select} value={firstNow ? "now" : "scheduled"} onChange={(e) => setFirstNow(e.target.value === "now")}><option value="now">Debit now</option><option value="scheduled">On schedule</option></select></div></div></>}
      <div className={styles.info}>{kind === "lumpsum" ? "The order will be created and the secure payment page will open in this dashboard tab." : "A bank e-mandate authorization page will open. The SIP is created after the approved mandate returns here."}</div></>}
    {error && <div className={styles.error}>{error}</div>}<div className={styles.dialogActions}><button className={styles.dialogCancel} onClick={onClose} disabled={submitting}>Close</button><button className={styles.dialogSubmit} disabled={!selected || !amountValid || submitting || detailsLoading} onClick={submit} aria-busy={submitting || undefined}>{submitting && <InlineLoader />} {submitting ? kind === "lumpsum" ? "Creating order…" : "Creating mandate…" : kind === "lumpsum" ? "Create & pay" : "Authorize mandate"}</button></div>
  </div></DialogContent></Dialog>;
};

const TransactionDialog = ({ open, kind, holding, sources, onClose, onComplete }: any) => {
  const sourceKey = (source: MFTransactionSource) => `${source.idField}:${source.id}`;
  const [amount, setAmount] = useState("");
  const [selectedSourceKey, setSelectedSourceKey] = useState(sources[0] ? sourceKey(sources[0]) : "");
  const [destination, setDestination] = useState("");
  const [sourceDetails, setSourceDetails] = useState<MFSchemeDetails | null>(null);
  const [destinationDetails, setDestinationDetails] = useState<MFSchemeDetails | null>(null);
  const [amcSchemes, setAmcSchemes] = useState<MFScheme[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [destinationLoading, setDestinationLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pendingConfirmation, setPendingConfirmation] = useState<{ id: string; kind: "redemption" | "switch" } | null>(null);

  useEffect(() => {
    let active = true;
    setRulesLoading(true);
    setSourceDetails(null);
    setDestinationDetails(null);
    setAmcSchemes([]);
    setDestination("");
    setAmount("");
    setError("");
    const loadRules = async () => {
      try {
        const [details, companies] = await Promise.all([
          fetchMFSchemeDetails(holding.isin),
          kind === "switch" ? fetchMFCompanies() : Promise.resolve([]),
        ]);
        if (!active) return;
        setSourceDetails(details);
        if (kind === "switch") {
          const normalizedIsin = String(holding.isin).trim().toUpperCase();
          const sourceSchemeDetails = details.schemes_details || details.schemes_detalis;
          const sourceAmcName = holding.amc ||
            sourceSchemeDetails?.amc_mst_new?.amc ||
            sourceSchemeDetails?.amc_mst_new?.fund ||
            null;
          const normalizedHoldingAmc = normalizeAmcName(sourceAmcName);
          const company = companies.find((candidate) =>
            (candidate.isin_list || []).some(
              (isin) => String(isin).trim().toUpperCase() === normalizedIsin
            )
          ) || companies.find((candidate) => {
            const normalizedCompany = normalizeAmcName(candidate.company_name);
            return Boolean(normalizedHoldingAmc && normalizedCompany && (
              normalizedCompany === normalizedHoldingAmc ||
              normalizedCompany.includes(normalizedHoldingAmc) ||
              normalizedHoldingAmc.includes(normalizedCompany)
            ));
          });
          if (!company?.company_name) {
            throw new Error("The source fund AMC could not be verified.");
          }
          const companySchemes = await fetchMFSchemesByFundName(company.company_name);
          if (!active) return;
          setAmcSchemes(companySchemes);
        }
      } catch (requestError) {
        if (active) setError(getMFErrorMessage(requestError));
      } finally {
        if (active) setRulesLoading(false);
      }
    };
    loadRules();
    return () => { active = false; };
  }, [holding.isin, kind]);

  useEffect(() => {
    let active = true;
    setDestinationDetails(null);
    if (!destination) return () => { active = false; };
    setDestinationLoading(true);
    setAmount("");
    fetchMFSchemeDetails(destination).then((details) => {
      if (!active) return;
      if (
        details.data?.switch_in_allowed !== true ||
        details.data?.active === false ||
        details.data?.merged === true
      ) {
        throw new Error("The destination scheme is not currently eligible for switch-in.");
      }
      setDestinationDetails(details);
      setError("");
    }).catch((requestError) => {
      if (active) setError(getMFErrorMessage(requestError));
    }).finally(() => {
      if (active) setDestinationLoading(false);
    });
    return () => { active = false; };
  }, [destination]);

  const destinations = amcSchemes.filter((scheme: MFScheme) =>
    scheme.isin !== holding.isin &&
    scheme.active !== false &&
    scheme.merged !== true &&
    scheme.switch_in_allowed === true
  );
  const selectedSource = sources.find((source: MFTransactionSource) => sourceKey(source) === selectedSourceKey);
  const sourceAvailable = Number(selectedSource?.redeemableAmount);
  const sourceDetailRules = getTransactionRules(
    sourceDetails?.schemes_details?.scheme_details ||
    sourceDetails?.schemes_detalis?.scheme_details
  );
  const sourceRules = Object.entries(holding.transactionRules).reduce(
    (rules, [key, value]) => value == null ? rules : { ...rules, [key]: value },
    sourceDetailRules
  );
  const destinationRules = getTransactionRules(
    destinationDetails?.schemes_details?.scheme_details ||
    destinationDetails?.schemes_detalis?.scheme_details
  );
  const minRule = kind === "redeem"
    ? sourceRules.minRedemptionAmount
    : sourceRules.minSwitchOutAmount && destinationRules.minSwitchInAmount
      ? Math.max(sourceRules.minSwitchOutAmount, destinationRules.minSwitchInAmount)
      : null;
  const stepRule = kind === "redeem"
    ? sourceRules.redemptionAmountMultiple
    : sourceRules.switchOutAmountMultiple && destinationRules.switchInAmountMultiple
      ? combineAmountMultiples(sourceRules.switchOutAmountMultiple, destinationRules.switchInAmountMultiple)
      : null;
  const min = minRule || 0;
  const max = Number.isFinite(sourceAvailable) && sourceAvailable > 0 ? sourceAvailable : 0;
  const amountStep = stepRule || 0;
  const sourceEligible = kind === "redeem"
    ? sourceDetails?.data?.redemption_allowed === true
    : sourceDetails?.data?.switch_out_allowed === true;
  const destinationEligible = kind === "redeem" || destinationDetails?.data?.switch_in_allowed === true;
  const sourceRulesAvailable = Boolean(
    kind === "redeem"
      ? sourceRules.minRedemptionAmount && sourceRules.redemptionAmountMultiple
      : sourceRules.minSwitchOutAmount && sourceRules.switchOutAmountMultiple
  );
  const destinationRulesAvailable = kind === "redeem" || Boolean(
    destination && destinationRules.minSwitchInAmount && destinationRules.switchInAmountMultiple
  );
  const authoritativeRulesAvailable = sourceRulesAvailable && destinationRulesAvailable;
  const amountRulesError = !rulesLoading && !destinationLoading && Boolean(
    (!sourceRulesAvailable && sourceDetails) ||
    (kind === "switch" && destination && destinationDetails && !destinationRulesAvailable)
  );
  const value = Number(amount);
  const valid = Boolean(
    sourceEligible &&
    destinationEligible &&
    authoritativeRulesAvailable &&
    selectedSource &&
    Number.isFinite(value) &&
    value >= min &&
    value <= max &&
    isAmountMultiple(value, amountStep) &&
    (kind === "redeem" || destinationDetails)
  );
  const submit = async () => {
    if (!selectedSource || (!pendingConfirmation && !valid)) {
      setError("Review the amount, source folio and destination before submitting.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      if (pendingConfirmation) {
        await confirmAdminTransaction(pendingConfirmation.kind, pendingConfirmation.id);
      } else {
        const result = kind === "redeem"
          ? await createAdminRedemption(value, selectedSource)
          : await createAdminSwitch(value, destination, selectedSource);
        if (!result.confirmed) {
          setPendingConfirmation({ id: result.id, kind: kind === "redeem" ? "redemption" : "switch" });
          setError(result.warning || `The ${kind} was created but still needs confirmation.`);
          return;
        }
      }
      await onComplete();
      onClose();
    } catch (requestError) {
      setError(getMFErrorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  };
  const changeSource = (value: string) => {
    setSelectedSourceKey(value);
    setAmount("");
    setPendingConfirmation(null);
    setError("");
  };
  return <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm"><DialogContent><DialogHeading title={kind === "redeem" ? "Redeem holding" : "Switch holding"} subtitle={`${holding.name} · no consent OTP`} onClose={onClose} /><div className={styles.form}><div className={styles.info}>The transaction limit below is for the selected folio, not the combined holding.</div>{rulesLoading && <div className={styles.info}><InlineLoader /> Loading authoritative transaction rules…</div>}<div className={styles.field}><label>Source folio</label><select className={styles.select} value={selectedSourceKey} onChange={(e) => changeSource(e.target.value)} disabled={Boolean(pendingConfirmation) || rulesLoading}>{sources.map((source: MFTransactionSource) => <option key={sourceKey(source)} value={sourceKey(source)}>Folio {source.folioNumber} · {source.redeemableAmount == null ? "availability unavailable" : `${currency(source.redeemableAmount)} available`}</option>)}</select>{!sources.length && <div className={styles.helper}>No successful purchase or SIP reference matches a redeemable folio for this holding.</div>}</div>{kind === "switch" && <div className={styles.field}><label>Destination scheme · same AMC</label><select className={styles.select} value={destination} disabled={Boolean(pendingConfirmation) || rulesLoading || destinationLoading} onChange={(e) => { setDestination(e.target.value); setAmount(""); setError(""); }}><option value="">Select destination</option>{destinations.map((scheme: MFScheme) => <option key={scheme.isin} value={scheme.isin}>{scheme.name}</option>)}</select>{destinationLoading && <div className={styles.helper}><InlineLoader /> Checking destination eligibility…</div>}{!rulesLoading && !error && !destinations.length && <div className={styles.helper}>No eligible switch-in scheme was returned for this AMC.</div>}</div>}<div className={styles.field}><label>Amount</label><input className={styles.input} type="number" min={min} max={max} step={amountStep || undefined} disabled={Boolean(pendingConfirmation) || rulesLoading || destinationLoading || !authoritativeRulesAvailable} value={amount} onChange={(e) => setAmount(e.target.value)} />{authoritativeRulesAvailable ? <div className={styles.helper}>Minimum {currency(min)} · Maximum {currency(max)} · Multiples of {currency(amountStep)}</div> : amountRulesError && <div className={styles.error}>{!sourceRulesAvailable ? `The source scheme did not return complete ${kind === "redeem" ? "redemption" : "switch-out"} amount rules.` : "The destination scheme did not return complete switch-in amount rules."}</div>}</div>{sourceDetails && !sourceEligible && <div className={styles.error}>This scheme does not currently allow {kind === "redeem" ? "redemptions" : "switch-outs"}.</div>}{destinationDetails && !destinationEligible && <div className={styles.error}>The selected destination does not currently allow switch-ins.</div>}{pendingConfirmation && <div className={styles.info}>Request {pendingConfirmation.id} was created. Retry confirmation to finish it; a new request will not be created.</div>}{error && <div className={styles.error}>{error}</div>}<div className={styles.dialogActions}><button className={styles.dialogCancel} onClick={onClose} disabled={submitting}>Close</button><button className={styles.dialogSubmit} disabled={(!pendingConfirmation && !valid) || submitting || rulesLoading || destinationLoading} onClick={submit} aria-busy={submitting || undefined}>{submitting && <InlineLoader />} {submitting ? pendingConfirmation ? "Confirming request…" : kind === "redeem" ? "Creating redemption…" : "Creating switch…" : pendingConfirmation ? "Retry confirmation" : kind === "redeem" ? "Submit redemption" : "Submit switch"}</button></div></div></DialogContent></Dialog>;
};

const OrderActionDialog = ({ open, action, onClose, onComplete }: any) => {
  const [reasonCode, setReasonCode] = useState("amount_not_available");
  const [customReason, setCustomReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { mode, order, skipInstruction, skipDateRange } = action;
  const toastr = require("toastr");
  const labels: Record<OrderActionMode, [string, string, React.ReactNode]> = { cancel_lumpsum: ["Cancel lumpsum order", "This is only available before allotment.", <ErrorOutlineIcon />], cancel_sip: ["Cancel SIP", "The purchase plan and linked mandate will be cancelled.", <ErrorOutlineIcon />], skip: ["Skip next SIP installment", `The SIP stays active. The ${formatDate(skipDateRange?.installmentDate)} installment will be skipped.`, <PauseCircleOutlineIcon />], resume: ["Resume SIP installment", "The active skip instruction will be cancelled.", <PlayCircleOutlineIcon />] };
  const submittingLabel: Record<OrderActionMode, string> = { cancel_lumpsum: "Cancelling order…", cancel_sip: "Cancelling SIP…", skip: "Skipping installment…", resume: "Resuming SIP…" };
  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (mode === "cancel_lumpsum") {
        await cancelAdminLumpsum(order.purchase_id);
      }
      if (mode === "cancel_sip") {
        const result = await cancelAdminSip({
          purchasePlanId: order.purchase_plan_id,
          mandateId: String(order.mandate_details?.id || order.payment_source || ""),
          cancellationCode: reasonCode,
          cancellationReason: customReason,
        });
        const warnings = [result.mandateCancellationWarning, result.syncWarning].filter(Boolean);
        if (warnings.length) {
          toastr.warning(`SIP cancellation accepted. ${warnings.join(" ")}`);
        }
      }
      if (mode === "skip") {
        if (!skipDateRange?.fromDate || !skipDateRange?.toDate) {
          throw new Error("The skip window could not be calculated.");
        }
        await skipAdminSip(order.purchase_plan_id, skipDateRange.fromDate, skipDateRange.toDate);
      }
      if (mode === "resume") {
        const id = String(skipInstruction?.id || "");
        if (!id) throw new Error("The active skip instruction is unavailable.");
        await resumeAdminSip(id);
      }
      await onComplete();
      onClose();
    } catch (requestError) {
      setError(getMFErrorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  };
  return <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs"><DialogContent><DialogHeading title={labels[mode][0]} subtitle={labels[mode][1]} onClose={onClose} /><div className={styles.form}><div className={styles.info}><b>{order.scheme_name || order.fund_name || order.scheme}</b><br />{currency(Number(order.amount || 0))}{isSip(order) ? ` · ${String(order.frequency || "SIP").replace(/_/g, " ")}` : ""}</div>{mode === "cancel_sip" && <><div className={styles.field}><label>Cancellation reason</label><select className={styles.select} value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}><option value="amount_not_available">Funds not available</option><option value="investment_returns_not_as_expected">Returns not as expected</option><option value="exit_load_not_as_expected">Exit load not as expected</option><option value="switch_to_other_scheme">Switch to another scheme</option><option value="fund_manager_changed">Fund manager changed</option><option value="investment_goal_complete">Investment goal complete</option><option value="mandate_not_ready">Mandate not ready</option><option value="invest_later">Invest later</option><option value="customer_support_not_satisfactory">Customer support not satisfactory</option><option value="amc_support_not_satisfactory">AMC support not satisfactory</option><option value="custom_reason">Other reason</option></select></div>{reasonCode === "custom_reason" && <div className={styles.field}><label>Reason</label><textarea className={styles.input} value={customReason} onChange={(e) => setCustomReason(e.target.value)} rows={3} /></div>}</>}{error && <div className={styles.error}>{error}</div>}<div className={styles.dialogActions}><button className={styles.dialogCancel} onClick={onClose} disabled={submitting}>Keep unchanged</button><button className={mode.startsWith("cancel") ? styles.dangerButton : styles.dialogSubmit} disabled={submitting || (reasonCode === "custom_reason" && !customReason.trim())} onClick={submit} aria-busy={submitting || undefined}>{submitting && <InlineLoader />} {submitting ? submittingLabel[mode] : mode === "skip" ? "Skip installment" : mode === "resume" ? "Resume SIP" : "Confirm cancellation"}</button></div></div></DialogContent></Dialog>;
};

/** Opened by uuid, so an admin limited to their own leads is checked first. */
const UserInvestmentsDashboardPage: React.FC = () => {
  const { userUuid } = useParams<RouteParams>();
  return (
    <InvestorLeadGate
      userUuid={userUuid}
      backTo={{ label: "Back to investor profiles", path: "/mf_investor_profile" }}
    >
      <UserInvestmentsDashboard />
    </InvestorLeadGate>
  );
};

export default UserInvestmentsDashboardPage;
